import type { ChronologyResolution } from './chronology.js';
import type { NormalizedChart, NormalizedHoroscope } from './domain.js';
import type { GeneratedHoroscopeReport } from './interpretation.js';
import { buildHoroscopeScopeEvidenceViewModel, type TimingScope } from './precisionPresentation.js';
import { topicInterpretationViewModel } from './presentation.js';
import { chronologyResolutionViewModel } from './calculationContextPresentation.js';
import { timingMethodologyViewModel } from './timingMethodology.js';
import { buildClaimExplanationPacket, type ClaimExplanationPacket } from './explainability.js';
import { extractNatalAndHoroscopeFeatures } from './horoscopeFeatureExtractor.js';
import { timingCoreConvergenceViewModel } from './timingCoreConvergence.js';

export interface ProductionTimingReaderDepth {
  model: 'PRODUCTION-TIMING-READER-0.1.0';
  status: 'available'|'structural_only'|'insufficient';
  headline: string;
  currentReading: string;
  layerReading: string;
  strengthExpression: string;
  pressureExpression: string;
  scenarioExamples: readonly string[];
  practicalApplication: string;
  boundary: string;
}

export interface ProductionTimingPageViewModel {
  model: 'PRODUCTION-TIMING-PAGE-0.2.0';
  targetSolarDate: string;
  targetTimeIndex: number;
  scopes: readonly ReturnType<typeof buildHoroscopeScopeEvidenceViewModel>[];
  topics: readonly ReturnType<typeof topicInterpretationViewModel>[];
  methodology: ReturnType<typeof timingMethodologyViewModel>;
  coreConvergence: ReturnType<typeof timingCoreConvergenceViewModel>;
  readerDepth: ProductionTimingReaderDepth;
  claimExplanations: readonly ClaimExplanationPacket[];
  chronology?: Readonly<{ birth: ReturnType<typeof chronologyResolutionViewModel>; target: ReturnType<typeof chronologyResolutionViewModel> }>;
  disclosure: string;
}

const TIMING_SCOPES: readonly TimingScope[] = Object.freeze(['decadal', 'minor', 'yearly', 'monthly', 'daily', 'hourly']);

function buildTimingReaderDepth(
  timingClaims: readonly Readonly<{ claimKey:string; ruleIds:readonly string[] }>[],
  coreConvergence: ReturnType<typeof timingCoreConvergenceViewModel>,
): ProductionTimingReaderDepth {
  const hasLifeFocus=timingClaims.some((claim)=>claim.claimKey==='timing.core_layer.life_focus_convergence');
  const ruleCount=timingClaims.reduce((sum,claim)=>sum+claim.ruleIds.length,0);
  const basis=ruleCount>1?'目前有多組已核准的跨層規則共同支持這個觀察焦點。':'目前有已核准的跨層規則支持這個觀察焦點。';
  if(hasLifeFocus){
    return Object.freeze({
      model:'PRODUCTION-TIMING-READER-0.1.0' as const,status:'available' as const,headline:'這段時間先抓重複出現的生活焦點',
      currentReading:`大限、小限、流年等核心時間層裡，至少兩層把注意力帶到同一個生活焦點。${basis}這代表它值得優先觀察與安排，不代表某件事已被預先決定。`,
      layerReading:'把大限看成較長期背景，小限與流年看成較近的推進層，再用流月、流日、流時確認短期節奏。只有多個層次反覆指向同一件事時，才提高閱讀優先度；單一天或單一流年不獨立升格成事件結論。',
      strengthExpression:'如果同一個主題在較長期計畫、近期責任與實際行程裡都反覆出現，最適合先集中資源處理一到兩個核心問題，減少同時開太多戰線。',
      pressureExpression:'如果只有短期層出現變動，或現實生活並沒有相同主題反覆出現，就先把它當成暫時波動；不要因為一個日期或一層訊號就提前做重大決定。',
      scenarioExamples:Object.freeze(['同一類責任在年度規劃、近期工作與本月安排裡反覆被排到前面。','原本想延後的家庭、工作或資源議題，隔一段時間又需要重新處理。','不同時間尺度都要求你回頭檢查同一項承諾、瓶頸或尚未完成的安排。']),
      practicalApplication:'先選一到兩個反覆出現的主題，寫下目前責任、下一個可執行步驟與重新檢查的時間點。之後用實際進展驗證是否真的持續成為焦點，而不是用命盤替你預先決定結果。',
      boundary:'運勢層只提供多時間尺度的觀察優先序；不替日期貼好壞標籤、不估算結果可能性，也不以單一流年替代現實判斷。',
    });
  }
  if(coreConvergence.repeatedFactCount>0){
    return Object.freeze({
      model:'PRODUCTION-TIMING-READER-0.1.0' as const,status:'structural_only' as const,headline:'目前先看跨時間層的重複結構',
      currentReading:`目前沒有已核准的正式運勢 Claim，但大限、小限、流年之間有 ${coreConvergence.repeatedFactCount} 組客觀重複結構。它只能提高觀察優先度，不能直接翻譯成好事、壞事或事件預告。`,
      layerReading:'先把重複結構當作「值得多看幾次」的標記，再回到實際生活確認同一主題是否真的跨不同時間尺度持續出現。',
      strengthExpression:'若現實中的責任、計畫與資源安排也持續指向同一主題，可集中整理優先順序與未完成事項。',
      pressureExpression:'若現實沒有相同主題，或只有單一短期變動，就維持觀察，不從結構重複自行推導事件結果。',
      scenarioExamples:Object.freeze(['同一件待辦在不同週期的規劃中反覆回來。','長期安排與近期責任都要求重新分配同一類資源。','短期波動出現時，先檢查它是否也存在於較長時間尺度。']),
      practicalApplication:'用固定週期回顧同一主題是否仍持續存在；只有現實證據也反覆出現時才提高處理優先度。',
      boundary:'這是客觀結構整理，不是正式事件判讀，也不把重複層數換算成結果可信程度。',
    });
  }
  return Object.freeze({
    model:'PRODUCTION-TIMING-READER-0.1.0' as const,status:'insufficient' as const,headline:'目前沒有足夠的多層訊號形成正式運勢判讀',
    currentReading:'目前六層資料沒有形成已核准的多層收斂判讀，因此系統不會用單一流年、流月或某一天補成事件預測。',
    layerReading:'仍可把大限到流時當作不同時間尺度的客觀盤面資料查看，但沒有跨層依據時不應硬湊成結論。',
    strengthExpression:'若之後多個時間層與現實安排都反覆指向同一主題，再提高它的閱讀優先度。',
    pressureExpression:'只有單層變動時保持觀察，不因日期本身改變重大醫療、法律、財務或人生決策。',
    scenarioExamples:Object.freeze(['某一天特別忙，但前後週期沒有同類問題。','單一流月出現變化，但年度責任並沒有同方向累積。','現實生活沒有對應主題時，不用為了符合命盤硬找事件。']),
    practicalApplication:'先依現實中的截止日、責任與可用資源安排事情；運勢資料只在多層重複時用來提醒你多做一次檢查。',
    boundary:'沒有足夠多層依據時保持留白；不替日期貼好壞標籤，也不推算結果可能性。',
  });
}

export function buildProductionTimingPageViewModel(
  chart: NormalizedChart,
  horoscope: NormalizedHoroscope,
  report: GeneratedHoroscopeReport,
  chronology?: Readonly<{ birth: ChronologyResolution; target: ChronologyResolution }>,
): ProductionTimingPageViewModel {
  if (chart.calculation.chartFingerprint !== horoscope.natalChartFingerprint) {
    throw new Error('Production timing page rejected horoscope bound to another natal chart');
  }
  if (report.targetSolarDate !== horoscope.targetSolarDate || report.targetTimeIndex !== horoscope.targetTimeIndex) {
    throw new Error('Production timing page rejected report/horoscope target mismatch');
  }
  const features = extractNatalAndHoroscopeFeatures(chart, horoscope);
  const timingClaims = report.topics.filter((topic) => topic.topic === 'timing').flatMap((topic) => topic.claims);
  const coreConvergence=timingCoreConvergenceViewModel(horoscope);
  return Object.freeze({
    model: 'PRODUCTION-TIMING-PAGE-0.2.0' as const,
    targetSolarDate: horoscope.targetSolarDate,
    targetTimeIndex: horoscope.targetTimeIndex,
    scopes: Object.freeze(TIMING_SCOPES.map((scope) => buildHoroscopeScopeEvidenceViewModel(chart, horoscope, scope))),
    topics: Object.freeze(report.topics.map(topicInterpretationViewModel)),
    methodology: timingMethodologyViewModel(),
    coreConvergence,
    readerDepth:buildTimingReaderDepth(timingClaims,coreConvergence),
    claimExplanations: Object.freeze(timingClaims.map((claim) => buildClaimExplanationPacket(claim, features))),
    ...(chronology ? { chronology: Object.freeze({
      birth: chronologyResolutionViewModel(chronology.birth),
      target: chronologyResolutionViewModel(chronology.target),
    }) } : {}),
    disclosure: '此頁逐層呈現大限、小限、流年、流月、流日、流時的宮位映射、四化與流耀；沒有來源的 timing 規則保持空白，不把單層訊號寫成必然事件。',
  });
}
